/**
 * Payment Failed Blocker Component
 *
 * Full-screen blocker shown when subscription payment has failed
 * and grace period has expired. Users cannot access the platform
 * until payment method is updated.
 *
 * Features:
 * - Shows days remaining in grace period (if applicable)
 * - Displays payment failure date
 * - "Update Payment Method" button → Stripe billing portal
 * - Cannot be dismissed - blocks all platform access
 * - Fully translated (FR/EN)
 *
 * Grace Period: 10 days (legally compliant)
 */

import React, { useState } from 'react';
import { AlertTriangle, CreditCard } from 'lucide-react';
import { useLanguage } from '@/contexts/language';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { api } from '@/services/apiClient';
import { logger } from '@/utils/logger';

interface PaymentFailedBlockerProps {
  paymentFailedAt: string;
  gracePeriodDaysRemaining: number;
  gracePeriodExpired: boolean;
}

export function PaymentFailedBlocker({
  paymentFailedAt,
  gracePeriodDaysRemaining,
  gracePeriodExpired
}: PaymentFailedBlockerProps) {
  const { t } = useLanguage();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUpdatePayment = async () => {
    setIsRedirecting(true);
    setError(null);

    try {
      // Redirect to Stripe billing portal to update payment method
      const { url } = await api.createBillingPortalSession();
      window.location.href = url;
    } catch (err) {
      logger.error('Failed to redirect to billing portal:', err);
      setError(t('subscription.paymentFailed.redirectError'));
      setIsRedirecting(false);
    }
  };

  const failedDate = new Date(paymentFailedAt).toLocaleDateString(
    t('settings.languageRegion.dateFormat') === 'MM/DD/YYYY' ? 'en-US' : 'fr-FR',
    { year: 'numeric', month: 'long', day: 'numeric' }
  );

  return (
    <div className="fixed inset-0 z-50 bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-destructive">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <CardTitle className="text-2xl">
            {t('subscription.paymentFailed.title')}
          </CardTitle>
          <CardDescription>
            {gracePeriodExpired
              ? t('subscription.paymentFailed.accessExpired')
              : t('subscription.paymentFailed.gracePeriodActive')}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Payment Failed Date */}
          <div className="bg-muted p-4 rounded-lg space-y-1">
            <p className="text-sm text-muted-foreground">
              {t('subscription.paymentFailed.failedOn')}
            </p>
            <p className="text-lg font-semibold">{failedDate}</p>
          </div>

          {/* Grace Period Countdown */}
          {!gracePeriodExpired && gracePeriodDaysRemaining > 0 && (
            <Alert>
              <AlertDescription>
                {t('subscription.paymentFailed.gracePeriodRemaining', {
                  days: gracePeriodDaysRemaining
                })}
              </AlertDescription>
            </Alert>
          )}

          {/* Access Expired Warning */}
          {gracePeriodExpired && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {t('subscription.paymentFailed.gracePeriodExpiredMessage')}
              </AlertDescription>
            </Alert>
          )}

          {/* What to do */}
          <div className="space-y-2">
            <p className="text-sm font-medium">
              {t('subscription.paymentFailed.whatToDo')}
            </p>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>{t('subscription.paymentFailed.step1')}</li>
              <li>{t('subscription.paymentFailed.step2')}</li>
              <li>{t('subscription.paymentFailed.step3')}</li>
            </ul>
          </div>

          {/* Error Message */}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </CardContent>

        <CardFooter className="flex flex-col space-y-2">
          <Button
            onClick={handleUpdatePayment}
            disabled={isRedirecting}
            className="w-full"
            size="lg"
          >
            <CreditCard className="mr-2 h-4 w-4" />
            {isRedirecting
              ? t('subscription.paymentFailed.redirecting')
              : t('subscription.paymentFailed.updatePaymentButton')}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            {t('subscription.paymentFailed.billingPortalNote')}
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
